import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';

export interface NotificationSchedule {
  time: string;
  days?: number;
  title?: string;
  body?: string;
}

class NotificationManager {
  private static instance: NotificationManager;

  private constructor() {}

  public static getInstance(): NotificationManager {
    if (!NotificationManager.instance) {
      NotificationManager.instance = new NotificationManager();
    }
    return NotificationManager.instance;
  }

  async requestPermissions(): Promise<boolean> {
    if (Capacitor.getPlatform() !== 'ios') return false;

    try {
      const permissionResult = await LocalNotifications.requestPermissions();
      return permissionResult.display === 'granted';
    } catch (error) {
      console.error('Failed to request notification permissions:', error);
      return false;
    }
  }

  async scheduleNotifications(schedule: NotificationSchedule): Promise<boolean> {
    if (Capacitor.getPlatform() !== 'ios') return false;

    try {
      // Clear existing notifications
      await this.clearAllNotifications();

      // Schedule new notifications
      const [hours, minutes] = schedule.time.split(':').map(Number);
      const notifications = Array.from({ length: schedule.days || 7 }, (_, i) => {
        const scheduledDate = new Date();
        scheduledDate.setDate(scheduledDate.getDate() + i);
        scheduledDate.setHours(hours, minutes, 0, 0);

        // If today's notification time has passed, push it to next week
        if (i === 0 && scheduledDate < new Date()) {
          scheduledDate.setDate(scheduledDate.getDate() + 7);
        }

        return {
          id: i + 1,
          title: "Luminous",
          body: schedule.body || "Time for your daily meditation",
          schedule: { at: scheduledDate },
          actionTypeId: 'OPEN_MEDITATION',
          extra: {
            day: i + 1
          }
        };
      });

      await LocalNotifications.schedule({ notifications });
      return true;
    } catch (error) {
      console.error('Failed to schedule notifications:', error);
      return false;
    }
  }

  async clearAllNotifications(): Promise<void> {
    if (Capacitor.getPlatform() !== 'ios') return;

    try {
      await LocalNotifications.removeAllDeliveredNotifications();
      const pendingNotifications = await LocalNotifications.getPending();
      if (pendingNotifications.notifications.length > 0) {
        await LocalNotifications.cancel({
          notifications: pendingNotifications.notifications
        });
      }
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  }

  async setupClickListener(onNotificationClick: () => void): Promise<void> {
    if (Capacitor.getPlatform() !== 'ios') return;

    try {
      await LocalNotifications.addListener(
        'localNotificationActionPerformed',
        (notification) => {
          if (notification.actionId === 'tap') {
            onNotificationClick();
          }
        }
      );
    } catch (error) {
      console.error('Failed to setup notification click listener:', error);
    }
  }

  async removeClickListener(): Promise<void> {
    if (Capacitor.getPlatform() !== 'ios') return;

    try {
      await LocalNotifications.removeAllListeners();
    } catch (error) {
      console.error('Failed to remove notification listeners:', error);
    }
  }
}

export const notificationManager = NotificationManager.getInstance();
